import { useMemo } from 'react';
import {
    AnyAsset,
    AnyDebt,
    AnyFinancialEntity,
    ExternalFinancialEntity,
    getName,
    isPlentyGoalAccount,
} from '../../financialEntities';
import { Goal } from '../../goals';
import { sampleFinancialEntities } from '../../sampleData';
import {
    assetsFromFEs,
    debtsFromFEs,
    useFinancialEntitiesContext,
    useHasNoFEs,
} from '../financialEntities';
import { goalsFromFragments } from '../goals';
import { CashflowFinancialEntity } from './types';

export const useFinancialEntityMap = () => {
    const { fes } = useFinancialEntitiesContext();
    const hasNoFEs = useHasNoFEs();

    const financialEntityMap = useMemo(() => {
        if (!fes.data) return;

        const financialEntities = hasNoFEs
            ? (sampleFinancialEntities as AnyFinancialEntity[])
            : fes.data.financialEntities;

        const assets = assetsFromFEs(financialEntities);
        const debts = debtsFromFEs(financialEntities);
        const goals = goalsFromFragments(fes.data.goals);

        return buildFinancialEntityMap(assets, debts, goals);
    }, [hasNoFEs, fes.data]);

    return {
        financialEntityMap,
        financialEntityMapLoading: fes.loading,
        refetch: fes.refetch,
    };
};

const buildFinancialEntityMap = (
    assets: AnyAsset[],
    debts: AnyDebt[],
    goals: Goal[]
) => {
    const map: Record<string, CashflowFinancialEntity> = {};

    for (const financialEntity of [...assets, ...debts]) {
        map[financialEntity.id] = buildFinancialEntity(financialEntity);
    }

    for (const goal of goals) {
        for (const financialEntity of goal.financialEntities) {
            if (isPlentyGoalAccount(financialEntity)) {
                // We want to show the name of Plenty IRA accounts, not the goal name (Retirement)
                const name = ['TRADITIONAL_IRA', 'ROTH_IRA'].includes(
                    financialEntity.info.accountType
                )
                    ? getName(financialEntity, { withTypeAsName: true })
                    : getName(financialEntity);

                map[financialEntity.id] = {
                    id: financialEntity.id,
                    name,
                    mask: financialEntity.mask,
                    institutionName: 'Plenty',
                    isMine: financialEntity.isMine,
                    ownershipType: financialEntity.ownershipType,
                    jointOwnerPartnershipId:
                        financialEntity.jointOwnerPartnershipId,
                    goal,
                };
            } else {
                map[financialEntity.id] = buildFinancialEntity(financialEntity);
            }
        }
    }

    return map;
};

const buildFinancialEntity = (
    financialEntity: ExternalFinancialEntity
): CashflowFinancialEntity => ({
    id: financialEntity.id,
    name: getName(financialEntity),
    mask: financialEntity.mask,
    institutionName: financialEntity.institution?.name,
    institutionLogo: financialEntity.institution?.logo,
    ownershipType: financialEntity.ownershipType,
    jointOwnerPartnershipId: financialEntity.jointOwnerPartnershipId,
    isMine: financialEntity.isMine,
});
